/**
 * Button Variant Enum
 * Defines all possible button style variants
 *
 * @example
 * ```tsx
 * <Button variant={ButtonVariant.Default}>Click me</Button>
 * <Button variant={ButtonVariant.Destructive}>Delete</Button>
 * ```
 */
export enum ButtonVariant {
  Default = 'default',
  Destructive = 'destructive',
  Outline = 'outline',
  Secondary = 'secondary',
  Ghost = 'ghost',
  Link = 'link',
}

/**
 * Button Size Enum
 * Defines all possible button sizes following WCAG 2.1 AA standards
 * All sizes meet minimum 44x44px touch target requirements
 *
 * @example
 * ```tsx
 * <Button size={ButtonSize.Small}>Small</Button>
 * <Button size={ButtonSize.Large}>Large</Button>
 * <Button size={ButtonSize.Icon}><Icon /></Button>
 * ```
 */
export enum ButtonSize {
  Default = 'default',
  Small = 'sm',
  Large = 'lg',
  Icon = 'icon',
}

/**
 * Type guard to check if a value is a valid ButtonVariant
 */
export function isButtonVariant(value: unknown): value is ButtonVariant {
  return (
    typeof value === 'string' &&
    Object.values(ButtonVariant).includes(value as ButtonVariant)
  );
}

/**
 * Type guard to check if a value is a valid ButtonSize
 */
export function isButtonSize(value: unknown): value is ButtonSize {
  return (
    typeof value === 'string' &&
    Object.values(ButtonSize).includes(value as ButtonSize)
  );
}

/**
 * Get all button variant values as an array
 */
export function getAllButtonVariants(): readonly ButtonVariant[] {
  return Object.values(ButtonVariant);
}

/**
 * Get all button size values as an array
 */
export function getAllButtonSizes(): readonly ButtonSize[] {
  return Object.values(ButtonSize);
}
